"use client";

import React from "react";
import { motion } from "framer-motion";
import { Calculator, Stethoscope, Scale, Wrench, Zap, Briefcase, ShoppingBag, Building2, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectorsData = [{
  id: "accountants",
  icon: Calculator,
  title: "Accountants & Financial Services",
  description: "Targeted SEO strategies attracting high-value corporate and private clients for tax advice, auditing, and wealth management services.",
  benefit: "High-intent lead generation",
  link: "/B2BSeoLeadGenerationPage"
}, {
  id: "dentists",
  icon: Stethoscope,
  title: "Dentists & Private Healthcare",
  description: "Local & national search visibility for cosmetic dentistry, orthodontics, and specialist medical treatments with strong patient conversion.",
  benefit: "Local Map Pack dominance",
  link: "/LocalSEOPage"
}, {
  id: "solicitors",
  icon: Scale,
  title: "Solicitors & Legal Practices",
  description: "Authority-focused SEO engineered to rank for competitive legal terms across commercial law, family law, and personal injury.",
  benefit: "High-trust content & EEAT",
  link: "/NationalSeoPage"
}, {
  id: "plumbers",
  icon: Wrench,
  title: "Plumbers & HVAC Engineers",
  description: "Hyper-targeted Local SEO and Google Map Pack optimisation capturing urgent callouts and lucrative installation projects.",
  benefit: "Immediate geo-targeted leads",
  link: "/LocalSEOPage"
}, {
  id: "electricians",
  icon: Zap,
  title: "Electricians & Contractors",
  description: "Proven search strategies driving qualified commercial contracts and high-margin domestic electrical jobs directly to you.",
  benefit: "Consistent service inquiries",
  link: "/LocalSEOPage"
}, {
  id: "b2b",
  icon: Briefcase,
  title: "B2B & Professional Services",
  description: "In-depth technical SEO and commercial content structures that position your consultancy or SaaS firm in front of key decision-makers.",
  benefit: "Pipeline growth & strategy",
  link: "/B2BSeoLeadGenerationPage"
}, {
  id: "ecommerce",
  icon: ShoppingBag,
  title: "Ecommerce & Online Retail",
  description: "Scalable collection hierarchy, technical product SEO, and structured data that increase organic revenue and shopping visibility.",
  benefit: "Sustainable organic revenue",
  link: "/EcommerceSeoPage"
}, {
  id: "realestate",
  icon: Building2,
  title: "Estate Agents & Property",
  description: "Geo-targeted SEO attracting property sellers, buyers, and landlords across competitive regional and national property markets.",
  benefit: "High-value valuation leads",
  link: "/NationalSeoPage"
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Sectors() {
  return <section id="industrysectorsgrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28 transition-colors duration-300">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3.5 py-1 text-xs md:text-sm font-medium tracking-wide rounded-full text-foreground bg-secondary">
            Sectors &amp; Expertise
          </Badge>

          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            Specialised SEO Strategies Tailored to Your Industry
          </h1>

          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            I work directly with business owners, practice leaders, and marketing managers across key sectors. Every campaign is built around your specific audience intent and commercial targets.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sectorsData.map((sector, i) => {
          const IconComponent = sector.icon;
          return <motion.div key={sector.id} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border/60 bg-card hover:border-primary/40 hover:shadow-lg transition-[border-color,box-shadow] duration-300 flex flex-col justify-between group rounded-xl">
                  <CardHeader className="p-6 pb-2">
                    {}
                    <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      <IconComponent className="size-6 shrink-0" />
                    </div>

                    <CardTitle className="text-lg md:text-xl font-semibold text-foreground font-serif leading-snug">
                      {sector.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-6 pt-2 flex-1">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mb-4">
                      {sector.description}
                    </CardDescription>

                    <div className="flex items-center gap-2 text-xs font-medium text-foreground/80 bg-muted/60 px-3 py-1.5 rounded-md w-fit">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      <span>{sector.benefit}</span>
                    </div>
                  </CardContent>

                  <CardFooter className="p-6 pt-0 border-t border-border/40 mt-auto">
                    <Button variant="ghost" size="sm" asChild className="w-full justify-between px-0 text-primary hover:bg-transparent hover:text-primary/80 font-medium group/btn">
                      <Link to={sector.link}>
                        <span>Explore Strategy</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {}
        <div className="mt-16 bg-muted/40 border border-border/60 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left max-w-2xl">
            <h3 className="text-xl md:text-2xl font-semibold text-foreground font-serif">
              {"Don't see your specific sector listed?"}
            </h3>
            <p className="text-muted-foreground text-sm md:text-base">
              Every SEO campaign I build is customized from scratch based on competitor analysis and search volume. You work directly with me to define a clear, realistic growth plan.
            </p>
          </div>

          <Button size="lg" asChild className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 font-medium shadow-sm rounded-lg px-6">
            <Link to="/ContactPage">
              Book a Free SEO Consultation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}